import React, { useState, useRef, useEffect } from 'react';
import { Language } from '../data/translations';

interface LanguageSwitcherProps {
    currentLanguage: Language;
    onLanguageChange: (lang: Language) => void;
}

const GlobeIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
);

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ currentLanguage, onLanguageChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const languages: { code: Language; name: string }[] = [
        { code: 'en', name: 'English' },
        { code: 'cn', name: '中文' },
        { code: 'uk', name: 'Українська' },
        { code: 'ru', name: 'Русский' },
    ];

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="relative pointer-events-auto" ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 backdrop-blur-md border shadow-lg cursor-pointer
                    ${isOpen 
                        ? 'bg-indigo-600/90 text-white border-indigo-400/50 scale-105' 
                        : 'bg-black/40 text-slate-300 border-white/10 hover:bg-white/10 hover:text-white hover:border-white/30'
                    }
                `}
                title="Change Language"
            >
                <GlobeIcon className="w-5 h-5" />
                <span className="text-[14px] font-bold uppercase">{currentLanguage}</span>
            </button>

            {isOpen && (
                <div className="absolute top-full right-0 mt-2 flex flex-col gap-1 min-w-[140px] p-1.5 bg-black/80 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl animate-[fadeIn_0.2s_ease-out] z-50">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onLanguageChange(lang.code);
                                setIsOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 rounded-xl text-[15px] font-medium transition-all duration-200 cursor-pointer
                                ${currentLanguage === lang.code
                                    ? 'bg-indigo-600/80 text-white shadow-lg shadow-indigo-500/20'
                                    : 'text-slate-300 hover:bg-white/10 hover:text-white'
                                }
                            `}
                        >
                            {lang.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LanguageSwitcher;
